import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Gauge,
  Maximize2,
  MapPin,
  Pause,
  Play,
  Repeat,
  RotateCcw,
  Volume2,
  VolumeX,
} from 'lucide-react'
import { mediaUrl } from '../lib/api'
import { clock, cx, preciseClock } from '../lib/format'
import type { Evidence, Marker } from '../lib/types'

const SPEEDS = [0.25, 0.5, 1, 1.5, 2]

export function EvidencePlayer({
  caseId,
  evidence,
  markers,
  occurredAt,
  onAddMarker,
  className,
}: {
  caseId: string
  evidence: Evidence
  markers: Marker[]
  occurredAt?: string | null
  onAddMarker?: (timeSec: number) => void
  className?: string
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const shellRef = useRef<HTMLDivElement | null>(null)
  const trackRef = useRef<HTMLDivElement | null>(null)

  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(evidence.durationSec ?? 0)
  const [buffered, setBuffered] = useState(0)
  const [speed, setSpeed] = useState(1)
  const [muted, setMuted] = useState(true)
  const [looping, setLooping] = useState(false)
  const [scrubPreview, setScrubPreview] = useState<number | null>(null)
  const [ready, setReady] = useState(false)

  const frameStep = useMemo(() => 1 / (evidence.frameRate || 30), [evidence.frameRate])
  const ownMarkers = useMemo(
    () => markers.filter((marker) => marker.evidenceId === evidence.id).sort((a, b) => a.timeSec - b.timeSec),
    [markers, evidence.id],
  )

  const src = mediaUrl(caseId, evidence.file)
  const poster = evidence.poster ? mediaUrl(caseId, evidence.poster) : undefined

  const withVideo = useCallback((action: (video: HTMLVideoElement) => void) => {
    const video = videoRef.current
    if (video) action(video)
  }, [])

  const togglePlay = useCallback(() => {
    withVideo((video) => {
      if (video.paused) void video.play()
      else video.pause()
    })
  }, [withVideo])

  const seekTo = useCallback(
    (seconds: number) => {
      withVideo((video) => {
        const limit = video.duration || duration || 0
        video.currentTime = Math.max(0, Math.min(limit ? limit - 0.001 : seconds, seconds))
        setCurrentTime(video.currentTime)
      })
    },
    [withVideo, duration],
  )

  const nudge = useCallback(
    (delta: number) => {
      withVideo((video) => {
        video.pause()
        seekTo(video.currentTime + delta)
      })
    },
    [seekTo, withVideo],
  )

  useEffect(() => {
    withVideo((video) => {
      video.playbackRate = speed
    })
  }, [speed, withVideo])

  // Keyboard shortcuts, scoped to when the player is on screen and the user is
  // not typing into a field.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return
      if (!shellRef.current) return

      switch (event.key) {
        case ' ':
        case 'k':
          event.preventDefault()
          togglePlay()
          break
        case 'ArrowLeft':
          event.preventDefault()
          nudge(event.shiftKey ? -10 : -1)
          break
        case 'ArrowRight':
          event.preventDefault()
          nudge(event.shiftKey ? 10 : 1)
          break
        case ',':
          event.preventDefault()
          nudge(-frameStep)
          break
        case '.':
          event.preventDefault()
          nudge(frameStep)
          break
        case 'm':
          setMuted((value) => !value)
          break
        case 'f':
          void shellRef.current.requestFullscreen?.()
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [togglePlay, nudge, frameStep])

  const progress = duration ? (currentTime / duration) * 100 : 0

  const timeFromPointer = (clientX: number) => {
    const track = trackRef.current
    if (!track || !duration) return 0
    const rect = track.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    return ratio * duration
  }

  // Wall-clock time of the frame on screen, derived from when the clip started.
  const stampedTime = useMemo(() => {
    if (!occurredAt) return null
    const base = new Date(occurredAt).getTime()
    if (Number.isNaN(base)) return null
    return new Date(base + currentTime * 1000).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'America/Los_Angeles',
    })
  }, [occurredAt, currentTime])

  return (
    <div ref={shellRef} className={cx('max-w-full overflow-hidden rounded-xl border border-white/[0.08] bg-black', className)}>
      <div className="group relative bg-black">
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          preload="metadata"
          playsInline
          muted={muted}
          loop={looping}
          className="block max-h-[68vh] w-full bg-black"
          onClick={togglePlay}
          onLoadedMetadata={(event) => {
            const video = event.currentTarget
            if (Number.isFinite(video.duration)) setDuration(video.duration)
            setReady(true)
          }}
          onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
          onProgress={(event) => {
            const video = event.currentTarget
            if (video.buffered.length && video.duration) {
              setBuffered((video.buffered.end(video.buffered.length - 1) / video.duration) * 100)
            }
          }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        />

        {/* Overlay HUD */}
        <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-3 bg-gradient-to-b from-black/70 to-transparent p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="pill border-red-500/40 bg-black/60 text-red-300 backdrop-blur">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-pulse-ring rounded-full bg-red-500" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-red-500" />
              </span>
              Evidence
            </span>
            {evidence.source ? (
              <span className="pill border-white/15 bg-black/50 text-slate-300 backdrop-blur">
                {evidence.source}
              </span>
            ) : null}
          </div>
          <div className="text-right font-mono text-[11.5px] leading-tight text-white/80 drop-shadow">
            {stampedTime ? <div className="text-[13px] font-semibold text-white">{stampedTime}</div> : null}
            <div>{preciseClock(currentTime)}</div>
            {evidence.width ? (
              <div className="text-white/50">
                {evidence.width}×{evidence.height}
                {evidence.frameRate ? ` · ${Math.round(evidence.frameRate)}fps` : ''}
              </div>
            ) : null}
          </div>
        </div>

        {!playing ? (
          <button
            type="button"
            onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center transition"
            aria-label="Play video"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-white/25 bg-black/55 text-white backdrop-blur-sm transition hover:scale-105 hover:bg-altair-500/90">
              <Play size={26} className="ml-1" fill="currentColor" />
            </span>
          </button>
        ) : null}
      </div>

      {/* Timeline */}
      <div className="border-t border-white/[0.07] bg-ink-900 px-3 pb-2 pt-3">
        <div className="relative">
          <div
            ref={trackRef}
            className="group/track relative h-9 cursor-pointer"
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture(event.pointerId)
              seekTo(timeFromPointer(event.clientX))
            }}
            onPointerMove={(event) => {
              setScrubPreview(timeFromPointer(event.clientX))
              if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                seekTo(timeFromPointer(event.clientX))
              }
            }}
            onPointerLeave={() => setScrubPreview(null)}
          >
            <div className="absolute inset-x-0 top-4 h-1.5 overflow-hidden rounded-full bg-white/[0.09]">
              <div className="h-full bg-white/10" style={{ width: `${buffered}%` }} />
            </div>
            <div
              className="absolute top-4 h-1.5 rounded-full bg-altair-500"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute top-4 -ml-[7px] h-1.5 w-1.5"
              style={{ left: `${progress}%` }}
              aria-hidden
            >
              <span className="absolute -top-[5px] left-0 h-3 w-3 rounded-full border-2 border-white bg-altair-500 shadow-[0_0_0_4px_rgba(239,78,35,0.25)]" />
            </div>

            {/* Key moment markers */}
            {ownMarkers.map((marker) => {
              const left = duration ? Math.min(100, (marker.timeSec / duration) * 100) : 0
              return (
                <button
                  key={marker.id}
                  type="button"
                  title={`${clock(marker.timeSec)} — ${marker.label}`}
                  onPointerDown={(event) => {
                    event.stopPropagation()
                    seekTo(marker.timeSec)
                  }}
                  className="absolute top-[9px] -ml-[5px] h-4 w-2.5 rounded-sm border border-ink-900 bg-amber-400 transition hover:h-5 hover:bg-amber-300"
                  style={{ left: `${left}%` }}
                  aria-label={`Jump to ${marker.label}`}
                />
              )
            })}

            {scrubPreview !== null ? (
              <div
                className="pointer-events-none absolute -top-1 -translate-x-1/2 rounded bg-ink-700 px-1.5 py-0.5 font-mono text-[10.5px] text-white shadow-lift"
                style={{ left: `${duration ? (scrubPreview / duration) * 100 : 0}%` }}
              >
                {clock(scrubPreview)}
              </div>
            ) : null}
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <button type="button" onClick={togglePlay} className="btn-subtle p-2" aria-label={playing ? 'Pause' : 'Play'}>
            {playing ? <Pause size={17} fill="currentColor" /> : <Play size={17} fill="currentColor" />}
          </button>

          <div className="flex items-center rounded-lg border border-white/[0.08] bg-white/[0.02]">
            <button
              type="button"
              onClick={() => nudge(-frameStep)}
              className="btn-subtle rounded-r-none px-2 py-1.5"
              title="Previous frame (,)"
            >
              <ChevronLeft size={15} />
            </button>
            <span className="px-1 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
              frame
            </span>
            <button
              type="button"
              onClick={() => nudge(frameStep)}
              className="btn-subtle rounded-l-none px-2 py-1.5"
              title="Next frame (.)"
            >
              <ChevronRight size={15} />
            </button>
          </div>

          <button type="button" onClick={() => seekTo(0)} className="btn-subtle p-2" title="Back to start">
            <RotateCcw size={15} />
          </button>

          <span className="ml-1 font-mono text-[12px] tabular-nums text-slate-400">
            {clock(currentTime)} <span className="text-slate-600">/ {clock(duration)}</span>
          </span>

          <div className="ml-auto flex items-center gap-1.5">
            <div className="flex items-center gap-1 rounded-lg border border-white/[0.08] bg-white/[0.02] px-2 py-1">
              <Gauge size={13} className="text-slate-500" />
              {SPEEDS.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setSpeed(value)}
                  className={cx(
                    'rounded px-1.5 py-0.5 text-[11.5px] font-semibold transition',
                    speed === value ? 'bg-altair-500 text-white' : 'text-slate-400 hover:text-white',
                  )}
                >
                  {value}×
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setLooping((value) => !value)}
              className={cx('btn-subtle p-2', looping && 'text-altair-400')}
              title="Loop"
            >
              <Repeat size={15} />
            </button>

            <button
              type="button"
              onClick={() => setMuted((value) => !value)}
              className="btn-subtle p-2"
              title="Mute (m)"
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>

            {onAddMarker ? (
              <button
                type="button"
                onClick={() => onAddMarker(currentTime)}
                className="btn-ghost px-2.5 py-1.5 text-[12.5px]"
                title="Flag this moment"
              >
                <MapPin size={14} />
                Mark moment
              </button>
            ) : null}

            <a
              href={src}
              download={evidence.originalName}
              className="btn-subtle p-2"
              title="Download original file"
            >
              <Download size={15} />
            </a>

            <button
              type="button"
              onClick={() => void shellRef.current?.requestFullscreen?.()}
              className="btn-subtle p-2"
              title="Fullscreen (f)"
            >
              <Maximize2 size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Marker list */}
      {ownMarkers.length ? (
        <div className="border-t border-white/[0.07] bg-ink-850 px-3 py-2.5">
          <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500">
            Key moments — click to jump
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ownMarkers.map((marker) => (
              <button
                key={marker.id}
                type="button"
                onClick={() => seekTo(marker.timeSec)}
                className={cx(
                  'chip transition hover:border-amber-400/40 hover:text-white',
                  Math.abs(currentTime - marker.timeSec) < 0.4 && 'border-amber-400/50 bg-amber-400/10 text-white',
                )}
              >
                <span className="font-mono text-[11.5px] text-amber-300">{clock(marker.timeSec)}</span>
                {marker.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {!ready && !poster ? (
        <div className="border-t border-white/[0.07] px-3 py-2 text-[12px] text-slate-500">
          Loading {evidence.originalName}…
        </div>
      ) : null}
    </div>
  )
}
